import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { Address } from '../types';

export interface ClientCompanyRecord {
  name: string;
  billingAddress: Address | null;
}

export function clientCompanyDocId(name: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return (slug || 'company').slice(0, 120);
}

const emptyAddress = (): Address => ({
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'United States',
});

function addressFromData(raw: unknown): Address | null {
  if (!raw || typeof raw !== 'object') return null;
  const addr = raw as Partial<Address>;
  const billingAddress: Address = {
    street: String(addr.street || '').trim(),
    city: String(addr.city || '').trim(),
    state: String(addr.state || '').trim(),
    zipCode: String(addr.zipCode || '').trim(),
    country: String(addr.country || 'United States').trim() || 'United States',
  };
  if (!billingAddress.street && !billingAddress.city && !billingAddress.state && !billingAddress.zipCode) {
    return null;
  }
  return billingAddress;
}

export async function loadClientCompany(companyName: string): Promise<ClientCompanyRecord | null> {
  const name = companyName.trim();
  if (!name) return null;
  const snap = await getDoc(doc(db, 'clientCompanies', clientCompanyDocId(name)));
  if (!snap.exists()) return { name, billingAddress: null };
  const data = snap.data();
  return {
    name: String(data.name || name),
    billingAddress: addressFromData(data.billingAddress),
  };
}

export async function saveClientCompanyBillingAddress(companyName: string, address: Address): Promise<void> {
  const name = companyName.trim();
  if (!name) throw new Error('Add a company name first.');
  const billingAddress = addressFromData(address) || emptyAddress();
  const hasAddress = !!(billingAddress.street || billingAddress.city || billingAddress.state || billingAddress.zipCode);
  await setDoc(doc(db, 'clientCompanies', clientCompanyDocId(name)), {
    name,
    billingAddress: hasAddress ? billingAddress : null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}
